import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Crown, Copy, ArrowLeft, ArrowRight, Check, Loader2, ShieldAlert, ShieldCheck,
  Wifi, WifiOff, Copy as CopyIcon, AlertTriangle, KeyRound,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EXCHANGES, getExchange, MARKETS } from "@/data/exchanges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const STEPS = ["Type", "Exchange", "Details", "API Keys", "Permissions", "Test", "Confirm"];

export default function AddExchange() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [form, setForm] = useState({
    account_role: "", exchange_slug: "", account_name: "", description: "",
    market: "futures", api_key: "", api_secret: "", trading_enabled: true,
    withdrawal_enabled: false, is_demo: false, master_account_id: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: masters = [] } = useQuery({
    queryKey: ["masters"], queryFn: async () => (await api.get("/exchange-accounts/masters")).data,
    enabled: form.account_role === "copy",
  });
  const { data: trusted } = useQuery({ queryKey: ["trusted-ips"], queryFn: async () => (await api.get("/config/trusted-ips")).data });

  const canNext = () => {
    if (step === 0) return !!form.account_role && (form.account_role === "master" || !!form.master_account_id);
    if (step === 1) return !!form.exchange_slug;
    if (step === 2) return form.account_name.trim().length > 1;
    if (step === 3) return form.api_key.trim() && form.api_secret.trim();
    if (step === 5) return testResult !== null;
    return true;
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/exchange-accounts/test-connection", {
        exchange_slug: form.exchange_slug, market: form.market,
        api_key: form.api_key, api_secret: form.api_secret,
      });
      setTestResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setTestResult({ ok: false, code: "network", message: formatApiError(e) });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    const connection_status = testResult?.ok ? "active" : "unverified";
    try {
      const payload = {
        account_role: form.account_role, exchange_slug: form.exchange_slug,
        account_name: form.account_name, description: form.description, market: form.market,
        api_key: form.api_key, api_secret: form.api_secret,
        trading_enabled: form.trading_enabled, withdrawal_enabled: form.withdrawal_enabled,
        is_demo: form.is_demo, connection_status,
        master_account_id: form.account_role === "copy" ? form.master_account_id : null,
      };
      const { data } = await api.post("/exchange-accounts", payload);
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success(connection_status === "unverified" ? "Account saved as Unverified" : "Account created & verified");
      navigate(`/exchanges/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === STEPS.length - 1) { save(); return; }
    setStep((s) => s + 1);
  };
  const back = () => (step === 0 ? navigate("/exchanges") : setStep((s) => s - 1));

  const ex = getExchange(form.exchange_slug);

  return (
    <div>
      <PageHeader title="Add Exchange Account" subtitle="Connect a master or copy account in a few guided steps." pageKey="exchanges" testid="add-exchange-header" />

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={cn("flex items-center gap-2 flex-shrink-0", i <= step ? "text-[#D4AF37]" : "text-[#525252]")} data-testid={`wizard-step-${i}`}>
              <span className={cn("w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-mono font-medium",
                i < step ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]" : i === step ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#333333] text-[#525252]")}>
                {i < step ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              <span className="text-xs font-medium whitespace-nowrap hidden md:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={cn("h-px flex-1 min-w-[10px]", i < step ? "bg-[#D4AF37]" : "bg-[#262626]")} />}
          </React.Fragment>
        ))}
      </div>

      <div className="rounded-md border border-[#262626] bg-[#141414] p-6 min-h-[360px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>

            {step === 0 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5] mb-1">Account type</h3>
                <p className="text-sm text-[#A3A3A3] mb-5">Masters execute source trades; connected accounts mirror a master.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[{ v: "master", icon: Crown, t: "Master Account", d: "The source account whose trades are copied." },
                    { v: "copy", icon: Copy, t: "Connected Account", d: "Mirrors a master account automatically in real time." }].map((o) => (
                    <button key={o.v} onClick={() => set("account_role", o.v)} data-testid={`wizard-type-${o.v}`}
                      className={cn("text-left rounded-md border p-5 transition-colors", form.account_role === o.v ? "border-[#D4AF37] bg-[rgba(212,175,55,0.06)]" : "border-[#333333] hover:border-[#525252]")}>
                      <o.icon className={cn("w-6 h-6 mb-3", form.account_role === o.v ? "text-[#D4AF37]" : "text-[#A3A3A3]")} />
                      <div className="font-medium text-[#F5F5F5]">{o.t}</div>
                      <div className="text-xs text-[#737373] mt-1">{o.d}</div>
                    </button>
                  ))}
                </div>
                {form.account_role === "copy" && (
                  <div className="mt-5">
                    <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Link to master account</Label>
                    <select value={form.master_account_id} onChange={(e) => set("master_account_id", e.target.value)} data-testid="wizard-master-select"
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]">
                      <option value="">Select a master…</option>
                      {masters.map((m) => <option key={m.id} value={m.id}>{m.account_name} ({m.exchange_name})</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5] mb-1">Choose exchange</h3>
                <p className="text-sm text-[#A3A3A3] mb-5">Select where this account is hosted.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {EXCHANGES.map((e) => (
                    <button key={e.slug} onClick={() => { set("exchange_slug", e.slug); if (!e.markets.includes(form.market)) set("market", e.markets[0]); }}
                      data-testid={`wizard-exchange-${e.slug}`}
                      className={cn("text-left rounded-md border p-4 transition-colors", form.exchange_slug === e.slug ? "border-[#D4AF37] bg-[rgba(212,175,55,0.06)]" : "border-[#333333] hover:border-[#525252]")}>
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-sm bg-[#0A0A0A] border border-[#262626] flex items-center justify-center font-heading font-bold text-xs" style={{ color: e.color }}>{e.name.slice(0, 2).toUpperCase()}</span>
                        <span className="font-medium text-[#F5F5F5]">{e.name}</span>
                      </div>
                      <div className="text-[11px] text-[#737373] mt-2">{e.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="max-w-xl space-y-4">
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5]">Account details</h3>
                <div>
                  <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Account name</Label>
                  <Input value={form.account_name} onChange={(e) => set("account_name", e.target.value)} placeholder="e.g. KALKI_MASTER_BINANCE_01" data-testid="wizard-account-name"
                    className="bg-[#0A0A0A] border-[#333333] focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Description (optional)</Label>
                  <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Purpose of this account…" data-testid="wizard-description"
                    className="bg-[#0A0A0A] border-[#333333] focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] mb-1.5 block">Market</Label>
                  <select value={form.market} onChange={(e) => set("market", e.target.value)} data-testid="wizard-market"
                    className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]">
                    {MARKETS.filter((mk) => ex.markets.includes(mk.value)).map((mk) => <option key={mk.value} value={mk.value}>{mk.label}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-3 pt-1 cursor-pointer">
                  <Switch checked={form.is_demo} onCheckedChange={(v) => set("is_demo", v)} data-testid="wizard-demo" />
                  <span className="text-sm text-[#A3A3A3]">This is a demo / testnet account</span>
                </label>
              </div>
            )}

            {step === 3 && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4 max-w-md">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#F5F5F5]">API credentials</h3>
                    <p className="text-sm text-[#A3A3A3] mt-1">Keys are sent securely to our servers and never stored in your browser.</p>
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] mb-1.5 block">API Key</Label>
                    <Input value={form.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="Enter API key" data-testid="wizard-api-key"
                      className="bg-[#0A0A0A] border-[#333333] font-mono focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] mb-1.5 block">API Secret</Label>
                    <Input type="password" value={form.api_secret} onChange={(e) => set("api_secret", e.target.value)} placeholder="Enter API secret" data-testid="wizard-api-secret"
                      className="bg-[#0A0A0A] border-[#333333] font-mono focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
                  </div>
                </div>
                <div className="rounded-md border border-[#262626] bg-[#0D0D0D] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-sm font-medium text-[#F5F5F5]">Trusted IP whitelist</span>
                  </div>
                  <p className="text-xs text-[#A3A3A3] mb-3">When creating the API key on {ex.name}, restrict access to these IPs:</p>
                  <div className="space-y-2">
                    {(trusted?.ips || []).map((ip) => (
                      <div key={ip} className="flex items-center justify-between bg-[#141414] border border-[#262626] rounded-sm px-3 py-2">
                        <span className="font-mono text-sm text-[#F5F5F5]">{ip}</span>
                        <button onClick={() => { navigator.clipboard.writeText(ip); toast.success("Copied"); }} data-testid={`copy-ip-${ip}`} className="text-[#737373] hover:text-[#D4AF37] transition-colors">
                          <CopyIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="max-w-xl space-y-4">
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5]">Permissions</h3>
                <div className="rounded-md border border-[#262626] bg-[#0D0D0D] p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#F5F5F5]">Enable trading</div>
                    <div className="text-xs text-[#737373] mt-0.5">Allow Kalki to place orders on this account.</div>
                  </div>
                  <Switch checked={form.trading_enabled} onCheckedChange={(v) => set("trading_enabled", v)} data-testid="wizard-trading" />
                </div>
                <div className={cn("rounded-md border p-4", form.withdrawal_enabled ? "border-[rgba(239,68,68,0.4)] bg-[rgba(239,68,68,0.06)]" : "border-[#262626] bg-[#0D0D0D]")}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-[#F5F5F5]">Enable withdrawals</div>
                      <div className="text-xs text-[#737373] mt-0.5">Not required for copy-trading.</div>
                    </div>
                    <Switch checked={form.withdrawal_enabled} onCheckedChange={(v) => set("withdrawal_enabled", v)} data-testid="wizard-withdrawal" />
                  </div>
                  {form.withdrawal_enabled && (
                    <div className="mt-3 flex items-start gap-2 text-xs text-[#EF4444]" data-testid="withdrawal-warning">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Security risk: never grant withdrawal permission on API keys used for copy-trading. Kalki only needs Read + Trade access.</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="max-w-xl">
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5] mb-1">Test connection</h3>
                <p className="text-sm text-[#A3A3A3] mb-5">Verify credentials with {ex.name} before saving.</p>
                <button onClick={runTest} disabled={testing} data-testid="wizard-test-btn"
                  className="flex items-center gap-2 border border-[#333333] hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F5F5F5] text-sm rounded-sm px-4 py-2 transition-colors disabled:opacity-60">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                  {testing ? "Testing…" : "Run connection test"}
                </button>

                {testResult && (
                  <div className={cn("mt-5 rounded-md border p-4", testResult.ok ? "border-[rgba(16,185,129,0.3)] bg-[rgba(16,185,129,0.06)]" : testResult.code === "geo_restricted" ? "border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.06)]" : "border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.06)]")} data-testid="wizard-test-result">
                    <div className="flex items-start gap-3">
                      {testResult.ok ? <ShieldCheck className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                        : testResult.code === "geo_restricted" ? <ShieldAlert className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                        : <WifiOff className="w-5 h-5 text-[#EF4444] flex-shrink-0" />}
                      <div>
                        <div className={cn("text-sm font-medium", testResult.ok ? "text-[#10B981]" : testResult.code === "geo_restricted" ? "text-[#F59E0B]" : "text-[#EF4444]")}>
                          {testResult.ok ? "Connection verified" : testResult.code === "geo_restricted" ? "Region restricted — save as Unverified" : "Connection failed"}
                        </div>
                        <p className="text-xs text-[#A3A3A3] mt-1 leading-relaxed">{testResult.message}</p>
                        {testResult.balance != null && <p className="text-xs text-[#F5F5F5] mt-2 font-mono">Detected balance: ${testResult.balance.toLocaleString()}</p>}
                        {testResult.code === "geo_restricted" && <p className="text-xs text-[#F59E0B] mt-2">You can continue — the account will be validated later via Live Sync.</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 6 && (
              <div className="max-w-xl">
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5] mb-1">Confirm & create</h3>
                <p className="text-sm text-[#A3A3A3] mb-5">Review the account before saving.</p>
                <dl className="rounded-md border border-[#262626] bg-[#0D0D0D] divide-y divide-[#1c1c1c]" data-testid="wizard-summary">
                  {[
                    ["Type", form.account_role === "master" ? "Master" : "Connected"],
                    ["Exchange", ex.name],
                    ["Account name", form.account_name],
                    ["Market", form.market],
                    ["Trading", form.trading_enabled ? "Enabled" : "Disabled"],
                    ["Withdrawals", form.withdrawal_enabled ? "Enabled ⚠" : "Disabled"],
                    ["Connection", testResult?.ok ? "Verified" : "Unverified"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-2.5">
                      <dt className="text-xs text-[#737373]">{k}</dt>
                      <dd className="text-sm text-[#F5F5F5]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button onClick={back} data-testid="wizard-back-btn" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#F5F5F5] px-4 py-2 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
        </button>
        <button onClick={next} disabled={!canNext() || saving} data-testid="wizard-next-btn"
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-semibold text-sm rounded-sm px-5 py-2 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === STEPS.length - 1 ? <><KeyRound className="w-4 h-4" /> Create Account</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}
